import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, Heart, Shield } from "lucide-react";
import { trpc } from "@/lib/trpc";
import SearchResults from "./SearchResults";
import { useLocation } from "wouter";

export default function HeroSection() {
  const [searchZip, setSearchZip] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [searchResult, setSearchResult] = useState<any>(null);

  const searchMutation = trpc.serviceAreas.search.useQuery(
    { zipCode: searchZip },
    { enabled: false }
  );

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchZip.trim()) {
      return;
    }
    setShowResults(true);
    const result = await searchMutation.refetch();
    if (result.data) {
      setSearchResult(result.data);
    }
  };

  const handleClose = () => {
    setShowResults(false);
    setSearchResult(null);
    setSearchZip("");
  };

  const handleContactClick = () => {
    const contactSection = document.getElementById("contact-section");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-white py-12 md:py-16 overflow-hidden m-0 p-0">
      {/* Owner-supplied photo; original asset is uncropped and unaltered. */}
      <img
        src="/manus-storage/ez-team-truck-1536_1b77e4a5.webp"
        srcSet="/manus-storage/ez-team-truck-768_17f5d224.webp 768w, /manus-storage/ez-team-truck-1536_1b77e4a5.webp 1536w"
        sizes="100vw"
        alt=""
        width={1536}
        height={2048}
        fetchPriority="high"
        loading="eager"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover z-0"
        style={{ objectPosition: 'center 43%' }}
      />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary/65 to-primary/20" />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-primary/70 via-transparent to-primary/20" />

      <div className="container relative z-10">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-6">
            <div className="space-y-2">
              <p className="text-primary-foreground/80 text-sm font-semibold uppercase tracking-wide">
                Your Hometown Septic Experts
              </p>
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-tight text-white">
                Septic Made EZ, One Call Handles It All
              </h1>
            </div>

            <p className="text-base sm:text-lg text-primary-foreground/90 leading-relaxed">
              Do you have a residential or commercial septic system? Schedule with us, and we'll make sure your septic problems are covered now and forever.
            </p>
            <p className="text-sm sm:text-base text-accent font-semibold">
              🚨 Same-day emergency septic service available — call 980-922-PUMP
            </p>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4">
              <Button
                size="lg"
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
                onClick={() => {
                  window.location.href = '/schedule';
                }}
              >
                Schedule Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10"
                onClick={() => {
                  const servicesSection = document.getElementById('services-section');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Learn More
              </Button>
            </div>
          </div>

          {/* Right Content - Trust Badges */}
          <div className="space-y-6">
            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 flex items-center justify-center">
                <p className="text-sm font-semibold text-primary-foreground">Comprehensive Services</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 flex items-center justify-center">
                <p className="text-sm font-semibold text-primary-foreground">Local Experts</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-lg p-4 flex items-center justify-center">
                <p className="text-sm font-semibold text-primary-foreground">Ready to Help Now</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Family Owned and Veteran Owned - Bottom of Section */}
        <div className="flex flex-wrap gap-2 sm:gap-3 pt-12 justify-start">
          <div className="flex items-center gap-2 sm:gap-3 bg-accent text-accent-foreground px-4 sm:px-6 py-2 sm:py-3 rounded-xl shadow-lg font-bold text-sm sm:text-base">
            <Heart className="w-5 h-5 sm:w-6 sm:h-6" />
            Family Owned
          </div>
          <div className="flex items-center gap-2 sm:gap-3 bg-accent text-accent-foreground px-4 sm:px-6 py-2 sm:py-3 rounded-xl shadow-lg font-bold text-sm sm:text-base">
            <Shield className="w-5 h-5 sm:w-6 sm:h-6" />
            Veteran Owned
          </div>
        </div>
      </div>
    </section>
  );
}
